"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.readTealiumVisitorData = readTealiumVisitorData;
exports.setProductAnalyticsEnabled = setProductAnalyticsEnabled;
exports.setProductAnalyticsMetadata = setProductAnalyticsMetadata;
exports.replaceProductAnalyticsMetadata = replaceProductAnalyticsMetadata;
exports.track = track;
exports.page = page;
exports.resetProductAnalyticsForTests = resetProductAnalyticsForTests;
const PRODUCT_ANALYTICS_STATE_KEY = Symbol.for("@bestowinc/logger:product-analytics");
function isMetadata(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
// Keep this entry point dependency-free because independently deployed browser
// bundles each include it. This small guard avoids bundling Zod into every copy
// while safely recognizing the shared global state created by another bundle.
function isProductAnalyticsState(value) {
    return (typeof value === "object" &&
        value !== null &&
        "enabled" in value &&
        typeof value.enabled === "boolean" &&
        "metadata" in value &&
        isMetadata(value.metadata));
}
function getProductAnalyticsState() {
    // Reflect supports a runtime Symbol key without augmenting `globalThis` or
    // casting it to an application-specific interface.
    const existing = Reflect.get(globalThis, PRODUCT_ANALYTICS_STATE_KEY);
    if (isProductAnalyticsState(existing)) {
        return existing;
    }
    const state = {
        enabled: false,
        metadata: {},
    };
    // Store the state on the browser realm itself so separately bundled copies
    // resolve the same object through Symbol.for without a global type assertion.
    Reflect.set(globalThis, PRODUCT_ANALYTICS_STATE_KEY, state);
    return state;
}
// `utag` is a vendor-owned browser global with only two methods we consume.
// A narrow structural check is sufficient and keeps this client independent of
// Zod; host-provided JSON uses schemas at its actual serialization boundary.
function isUtag(value) {
    return (typeof value === "object" &&
        value !== null &&
        (!("link" in value) ||
            value.link === undefined ||
            typeof value.link === "function") &&
        (!("view" in value) ||
            value.view === undefined ||
            typeof value.view === "function"));
}
function getUtag() {
    // Tealium owns this dynamic global, so Reflect avoids declaring or casting a
    // permanent `globalThis.utag` property before narrowing its runtime value.
    const value = Reflect.get(globalThis, "utag");
    return isUtag(value) ? value : undefined;
}
function isUtagData(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function readTealiumPropensityData(value) {
    if (value === undefined) {
        return "{}";
    }
    if (typeof value === "string") {
        return value;
    }
    try {
        return JSON.stringify(value);
    }
    catch {
        return "{}";
    }
}
function getUtagData() {
    const utag = Reflect.get(globalThis, "utag");
    if (typeof utag !== "object" || utag === null) {
        return undefined;
    }
    const data = Reflect.get(utag, "data");
    return isUtagData(data) ? data : undefined;
}
const EMPTY_TEALIUM_VISITOR_DATA = {
    visitor_id: "",
    teal_ptb: "{}",
    visit_source: "",
};
/**
 * Read Tealium visitor/propensity fields at submit time. `utag.js` loads
 * asynchronously, so these values must not be captured at module init.
 */
function readTealiumVisitorData() {
    const utagData = getUtagData();
    if (!utagData) {
        return EMPTY_TEALIUM_VISITOR_DATA;
    }
    return {
        visitor_id: typeof utagData.tealium_visitor_id === "string"
            ? utagData.tealium_visitor_id
            : "",
        teal_ptb: readTealiumPropensityData(utagData.teal_ptb),
        visit_source: typeof utagData["cp.visit_source"] === "string"
            ? utagData["cp.visit_source"]
            : "",
    };
}
function setProductAnalyticsEnabled(enabled) {
    getProductAnalyticsState().enabled = enabled;
}
/**
 * Merge `metadata` into the traits sent with every event, matching V1's
 * `getMeta` accumulation. Callers that learn one trait later (e.g. the
 * `applicationID` assigned mid-enrollment) update only that key.
 */
function setProductAnalyticsMetadata(metadata) {
    const state = getProductAnalyticsState();
    state.metadata = { ...state.metadata, ...metadata };
}
/**
 * Overwrite every trait, dropping ones set earlier. For bootstrap/reset only —
 * the shell entry seeds the host-injected baseline this way so a soft reboot
 * can't inherit a previous session's traits.
 */
function replaceProductAnalyticsMetadata(metadata) {
    getProductAnalyticsState().metadata = { ...metadata };
}
function track(eventName, eventProperties = {}) {
    const state = getProductAnalyticsState();
    if (!state.enabled) {
        return;
    }
    const utag = getUtag();
    if (!utag?.link) {
        return;
    }
    try {
        utag.link({
            ...eventProperties,
            ...state.metadata,
            tealium_event: eventName,
        });
    }
    catch {
        // Analytics must never interrupt the enrollment experience.
    }
}
function page(pageName) {
    const state = getProductAnalyticsState();
    if (!state.enabled) {
        return;
    }
    const utag = getUtag();
    if (!utag?.view) {
        return;
    }
    try {
        utag.view({
            ...state.metadata,
            tealium_event: "page_view",
            page_name: pageName,
        });
    }
    catch {
        // Analytics must never interrupt the enrollment experience.
    }
}
function resetProductAnalyticsForTests() {
    Reflect.deleteProperty(globalThis, PRODUCT_ANALYTICS_STATE_KEY);
}
//# sourceMappingURL=product-analytics.client.js.map